import React, { useEffect, useMemo, Dispatch, useState } from 'react'
import { notification, Tabs } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { ApplicationState } from 'src/store'
import { Policy, PolicyTree } from 'src/types'
import * as PoliciesService from 'src/services/api/policies'
import * as PoliciesTreeService from 'src/services/api/policies-trees'

import * as CSS from './styles'

import { PropsTreeSelected } from '../..'
import HierarchyFlows from '../hierarchy-flows'
import HierarchyPolicies from '../hierarchy-policies'
import Roles from '../hierarchy-roles'

type Props = {
  dataSource?: any
  filterRole?: any
  loading?: any
  pagination?: any
  setFilterRole?: any
  setVisibleModal?: any
  treesSelected?: any
  setTreeSelected?: any
}

export type HierarchyProps = {
  policies: Policy[]
  policyTree: PolicyTree[]
  loading: boolean
  treesSelected: PropsTreeSelected
  setTreeSelected?: Dispatch<HierarchyProps['treesSelected']>
  resetPoliciesTrees: () => void
}

export default function TreeSideOptions({
  dataSource,
  filterRole,
  loading,
  pagination,
  setFilterRole,
  setVisibleModal,
  treesSelected,
  setTreeSelected
}: Props) {
  const [activeKey, setActiveKey] = useState<string>('roles')
  const [policies, setPolicies] = useState<Policy[]>([])
  const [policiesTrees, setPoliciesTrees] = useState<PolicyTree[]>([])

  const { t } = useTranslation()

  const { me } = useSelector((state: ApplicationState) => ({
    me: state.me
  }))

  function GetAllPolicies() {
    PoliciesService.getAll({
      organization_id: me.organization.organization_id,
      page: 1,
      withInactive: true
    })
      .then(response => {
        if (response.status === 200) {
          if (response.data) {
            setPolicies(response.data.policies)
          }
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
  }

  function GetPoliciesTrees() {
    PoliciesTreeService.getAll({
      organization_id: me.organization.organization_id,
      page: 1,
      withInactive: true
    })
      .then(response => {
        if (response.status === 200) {
          setPoliciesTrees(response.data.policy_trees)
        } else {
          notification.error({
            message: t('common.response.status-500'),
            placement: 'bottomRight'
          })
        }
      })
      .catch(response => {
        notification.error({
          message: t('common.response.status-500'),
          placement: 'bottomRight'
        })
      })
  }

  function ResetPolicyTrees() {
    GetPoliciesTrees()
  }

  useEffect(() => {
    GetAllPolicies()
    GetPoliciesTrees()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.organization.organization_id])

  const policyTree = useMemo(() => {
    if (policiesTrees) {
      return policiesTrees?.filter(e => e.assign_tree_id === treesSelected.id)
    }
    return []
  }, [treesSelected, policiesTrees])

  return (
    <CSS.Container>
      <Tabs
        activeKey={activeKey}
        data-test-id="air-result-panel-card"
        defaultActiveKey="roles"
        onChange={activeK => {
          setActiveKey(activeK)
        }}
        type="card"
      >
        <CSS.TabPane
          data-test-id="roles"
          key="roles"
          tab={t('web.tree.roles')}
        >
          <Roles
            dataSource={dataSource}
            filterRole={filterRole}
            loading={loading}
            pagination={pagination}
            setFilterRole={setFilterRole}
            setVisibleModal={setVisibleModal}
            treesSelected={treesSelected}
          />
        </CSS.TabPane>
        <CSS.TabPane
          data-test-id="flow"
          key="flow"
          tab={t('web.tree.flows.title')}
        >
          <HierarchyFlows
            loading={loading}
            policies={policies}
            policyTree={policyTree}
            resetPoliciesTrees={ResetPolicyTrees}
            setTreeSelected={setTreeSelected}
            treesSelected={treesSelected}
          />
        </CSS.TabPane>
        <CSS.TabPane
          data-test-id="policies"
          key="policies"
          tab={t('web.tree.policies.title')}
        >
          <HierarchyPolicies 
            treesSelected={treesSelected} 
            policies={policies} 
            policyTree={policyTree} 
            loading={loading} 
            resetPoliciesTrees={ResetPolicyTrees}
          />
        </CSS.TabPane>
      </Tabs>
    </CSS.Container>
  )
}
